/**
 * TextResult — generic in-app viewer for a SkuRun's stored markdown result.
 * Route: /me/result/:id
 *
 * Opened from "My Rites" when a purchase's ResultRef points to this route
 * (e.g. check_for_me, lucky, when, horary text results). For check-for-me
 * results the backend returns a `chatSeedRef`, which surfaces a «Обсудить»
 * button that opens the AI-astrologer chat grounded in this result.
 */
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MeSubpageShell } from '../../components/chrome/MeSubpageShell';
import { ReportMarkdown } from '../../components/ReportMarkdown';
import { PrimaryButton } from '../../components/chrome/Button';
import { useTextResult } from '../../hooks/useTextResult';

export function TextResult(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const { t } = useTranslation(['common', 'flows']);
  const navigate = useNavigate();
  const { data, isLoading, isError } = useTextResult(id);

  const title = data?.title ?? t('states.loading');

  return (
    <MeSubpageShell title={title} data-testid="text-result-page">
      {isError && <p role="alert">{t('errors.load')}</p>}
      {!isError && isLoading && <p>{t('states.loading')}</p>}
      {!isError && !isLoading && data && <ReportMarkdown markdown={data.markdown} />}
      {!isError && !isLoading && data?.chatSeedRef && (
        <PrimaryButton
          onClick={() => navigate(`/chat?surface=cfm&seedRef=${encodeURIComponent(data.chatSeedRef!)}`)}
          data-testid="text-result-discuss"
        >
          {t('check.discuss', { ns: 'flows' })}
        </PrimaryButton>
      )}
    </MeSubpageShell>
  );
}
